
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API_BASE = window.VAI_API_BASE || 'http://localhost:8000';
const api = axios.create({ baseURL: API_BASE, timeout: 300000 });
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('vai_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const ROLE_LABELS = {
  expert_vet:'Veteriner Uzman', farmers:'Çiftçi',
  animal_shelter_workers:'Barınak Çalışanı',
  livestock_industry_companies:'Hayvancılık Firması',
  pet_owners:'Evcil Hayvan Sahibi', research_scientist:'Araştırmacı',
};

const SUGGESTIONS = [
  { icon:'🐄', text:'Mastitis nasıl tedavi edilir?', role:'expert_vet' },
  { icon:'🐕', text:'Köpeklerde kuduz aşısı ne zaman yapılmalı?', role:'pet_owners' },
  { icon:'🐔', text:'Tavuklarda Newcastle hastalığı nasıl önlenir?', role:'livestock_industry_companies' },
  { icon:'🐑', text:'Koyunlarda kışın bakım nasıl yapılır?', role:'farmers' },
];

const LOADING_STEPS = [
  'Soru analiz ediliyor...','Rol ve risk seviyesi belirleniyor...',
  'RAG kaynakları getiriliyor...','Uzman ajanlar değerlendiriyor...',
  'İstişare turları tamamlanıyor...','Konsensüs oluşturuluyor...',
  'Raportör cevap hazırlıyor...',
];

function LoadingIndicator({ step }) {
  return (
    <div className="loading-container">
      <div className="loading-card">
        <div className="loading-header">
          <div className="loading-spinner" />
          <span className="loading-label">İstişare kurulu çalışıyor...</span>
        </div>
        <div className="loading-steps">
          {LOADING_STEPS.map((s, i) => (
            <div key={i} className={`loading-step ${i < step ? 'done' : i === step ? 'active' : ''}`}>
              <div className={`step-dot ${i < step ? 'done' : i === step ? 'active' : ''}`} />
              {i < step ? '✓ ' : ''}{s}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Beğen/Beğenme özelliği eklendi ───────────────────────
function AgentResponse({ data, feedbackState, onFeedback }) {
  const agentColors = {
    SurgicalAgent:'#f59e0b', InternalMedicineAgent:'#3b82f6',
    ReproductionAgent:'#ec4899', PharmacologyAgent:'#8b5cf6',
    PhysiologyAgent:'#06b6d4', NutritionAgent:'#10b981',
    MicrobiologyAgent:'#ef4444', ParasitologyAgent:'#f97316',
    PathologyAgent:'#64748b', VirologyAgent:'#dc2626',
    AnimalHusbandryAgent:'#84cc16',
  };
  const disciplineLabels = {
    surgery:'Cerrahi', internal_medicine:'İç Hastalıkları',
    reproduction:'Reprodüksiyon', pharmacology:'Farmakoloji',
    physiology:'Fizyoloji', nutrition:'Besleme',
    microbiology:'Mikrobiyoloji', parasitology:'Parazitoloji',
    pathology:'Patoloji', virology:'Viroloji',
    animal_husbandry:'Zootekni',
  };

  const voted = feedbackState; // null | 'up' | 'down'

  return (
    <div className="message-wrapper">
      <div className="message-assistant">
        <div className="message-assistant-header">
          <div className="assistant-avatar">🤖</div>
          <span className="assistant-label">VetAnimalAI İstişare Kurulu</span>
        </div>
        <div className="role-badge-response">🎯 {data.detected_role}</div>
        {data.is_emergency && (
          <div className="emergency-alert">
            🚨 ACİL DURUM: Lütfen derhal veteriner hekime başvurun!
          </div>
        )}
        {data.agent_evaluations && data.agent_evaluations.length > 0 && (
          <div className="agent-cards">
            {data.agent_evaluations.map((agent, i) => (
              <div className="agent-card" key={i}>
                <div className="agent-card-header">
                  <span className="agent-name-badge"
                    style={{
                      background:`${agentColors[agent.agent_name]}22`,
                      color:agentColors[agent.agent_name]||'#14b8a6',
                    }}>
                    {disciplineLabels[agent.discipline]||agent.agent_name}
                  </span>
                  <span style={{fontSize:'11px',color:'#5c6578'}}>Uzman Değerlendirmesi</span>
                </div>
                <div className="agent-card-body">{agent.evaluation}</div>
              </div>
            ))}
          </div>
        )}
        <div className="consensus-card">
          <div className="consensus-header">
            <div className="consensus-icon">
              <svg width="12" height="12" viewBox="0 0 12 12">
                <path d="M2 6l3 3 5-5" stroke="#0f1117" strokeWidth="1.5"
                      fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            İstişare Kurulu Nihai Değerlendirmesi
          </div>
          <div className="consensus-body">{data.consensus}</div>
        </div>
        {data.requires_vet && !data.is_emergency && (
          <div className="vet-required">
            ⚠️ Bu değerlendirme veteriner hekime başvuruyu önermektedir.
          </div>
        )}
        <div className="round-badge">
          🔄 İstişare kurulu bu karara&nbsp;
          <strong>{data.round_statement?.match(/\*\*(\d+) tur\*\*/)?.[1]||'?'} tur</strong>
          &nbsp;tartışma sonucunda ulaşmıştır.&nbsp;·&nbsp;
          <span style={{color:'#3b82f6'}}>{data.models_used?.join(' + ')}</span>
        </div>

        {/* ── Beğen / Beğenme ── */}
        <div className="feedback-row">
          {voted === null ? (
            <>
              <span className="feedback-label">Bu yanıt yardımcı oldu mu?</span>
              <button className="feedback-btn feedback-up" onClick={() => onFeedback('up')} title="Beğen">👍</button>
              <button className="feedback-btn feedback-down" onClick={() => onFeedback('down')} title="Beğenme">👎</button>
            </>
          ) : (
            <span className="feedback-thanks">
              {voted === 'up'
                ? '👍 Teşekkürler! Geri bildiriminiz kaydedildi.'
                : '👎 Teşekkürler! İyileştirme için not alındı.'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function AuthModal({ onClose, onSuccess }) {
  const [mode, setMode]       = useState('login');
  const [form, setForm]       = useState({email:'',password:'',username:'',role:'pet_owners'});
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const handle = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      if (mode === 'login') {
        const res = await api.post('/api/auth/login', {email:form.email, password:form.password});
        localStorage.setItem('vai_token', res.data.access_token);
        localStorage.setItem('vai_user', JSON.stringify(res.data.user));
        onSuccess(res.data.user);
      } else {
        await api.post('/api/auth/register', form);
        setMode('login'); alert('Kayıt başarılı! Giriş yapabilirsiniz.');
      }
    } catch(err) { setError(err.response?.data?.detail||'Bir hata oluştu.');
    } finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <div className="modal-title">{mode==='login'?'Giriş Yap':'Hesap Oluştur'}</div>
        <div className="modal-sub">{mode==='login'?'Sohbet geçmişinize erişmek için giriş yapın.':'Ücretsiz hesap oluşturun, sınırsız soru sorun.'}</div>
        {error && <div className="modal-error">{error}</div>}
        <form onSubmit={handle}>
          <div className="form-group">
            <label className="form-label">E-posta</label>
            <input className="form-input" type="email" required value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="ornek@email.com"/>
          </div>
          {mode==='register'&&(<>
            <div className="form-group">
              <label className="form-label">Kullanıcı Adı</label>
              <input className="form-input" type="text" required value={form.username} onChange={e=>setForm({...form,username:e.target.value})} placeholder="kullanici_adi"/>
            </div>
            <div className="form-group">
              <label className="form-label">Rol</label>
              <select className="form-select" value={form.role} onChange={e=>setForm({...form,role:e.target.value})}>
                {Object.entries(ROLE_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </>)}
          <div className="form-group">
            <label className="form-label">Şifre</label>
            <input className="form-input" type="password" required value={form.password} onChange={e=>setForm({...form,password:e.target.value})} placeholder="••••••••"/>
          </div>
          <button className="btn-primary" type="submit" disabled={loading}>
            {loading?'Lütfen bekleyin...':mode==='login'?'Giriş Yap':'Kayıt Ol'}
          </button>
        </form>
        <div className="modal-toggle">
          {mode==='login'?<>Hesabınız yok mu? <a onClick={()=>setMode('register')}>Kayıt olun</a></>:<>Zaten hesabınız var mı? <a onClick={()=>setMode('login')}>Giriş yapın</a></>}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser]           = useState(()=>{try{return JSON.parse(localStorage.getItem('vai_user'));}catch{return null;}});
  const [messages, setMessages]   = useState([]);
  const [conversations, setConvs] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [input, setInput]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [loadStep, setLoadStep]   = useState(0);
  const [showAuth, setShowAuth]   = useState(false);
  const [anonCount, setAnonCount] = useState(0);
  const [sessionId]               = useState(()=>crypto.randomUUID());
  const [feedbacks, setFeedbacks] = useState({});  // { assistantMsgIdx: 'up'|'down'|null }
  const chatRef                   = useRef(null);
  const textareaRef               = useRef(null);

  useEffect(()=>{
    if(user){ api.get('/api/chat/conversations').then(r=>setConvs(r.data.conversations||[])).catch(()=>{}); }
  },[user]);

  useEffect(()=>{
    if(chatRef.current) chatRef.current.scrollTop=chatRef.current.scrollHeight;
  },[messages,loading]);

  useEffect(()=>{
    if(!loading){setLoadStep(0);return;}
    const iv=setInterval(()=>setLoadStep(s=>Math.min(s+1,LOADING_STEPS.length-1)),2000);
    return ()=>clearInterval(iv);
  },[loading]);

  const logout=()=>{
    localStorage.removeItem('vai_token'); localStorage.removeItem('vai_user');
    setUser(null); setConvs([]); setMessages([]); setActiveConv(null); setFeedbacks({});
  };
  const newChat=()=>{ setMessages([]); setActiveConv(null); setInput(''); setFeedbacks({}); };

  // Feedback gönder
  const sendFeedback = useCallback(async (assistantIdx, vote, msgData) => {
    if (feedbacks[assistantIdx] !== undefined) return;
    setFeedbacks(prev=>({...prev, [assistantIdx]: vote}));
    try {
      // Backend'deki /api/feedback endpoint'ine gönder
      await api.post('/api/feedback', {
        message_id: msgData?.message_id || '',
        rating: vote === 'up' ? 1 : -1,
        comment: null,
      });
    } catch { /* sessizce devam */ }
  }, [feedbacks]);

  const sendMessage = useCallback(async (text) => {
    const q = text||input.trim();
    if(!q||loading) return;
    if(!user&&anonCount>=2){ setShowAuth(true); return; }
    setInput('');
    setMessages(prev=>[...prev,{type:'user',content:q}]);
    setLoading(true); setLoadStep(0);
    try {
      const res = await api.post('/api/chat/ask', {
        question:q, session_id:sessionId,
        conversation_id:activeConv, declared_role:user?.role,
      });
      const data = res.data;
      if(!activeConv&&data.conversation_id) setActiveConv(data.conversation_id);
      setMessages(prev=>[...prev,{type:'assistant',data}]);
      if(!user) setAnonCount(c=>c+1);
      if(user) api.get('/api/chat/conversations').then(r=>setConvs(r.data.conversations||[])).catch(()=>{});
    } catch(err) {
      const detail = err.response?.data?.detail;
      if(detail?.error==='anon_limit_reached'){ setShowAuth(true); setMessages(prev=>prev.slice(0,-1)); return; }
      setMessages(prev=>[...prev,{type:'assistant',data:{
        detected_role:'Sistem Hatası', agent_evaluations:[],
        consensus:`Bir hata oluştu: ${err.response?.data?.detail||err.message}. Lütfen tekrar deneyin.`,
        round_statement:'', requires_vet:false, is_emergency:false,
        confidence:0, safety_note:'', models_used:[],
      }}]);
    } finally { setLoading(false); }
  }, [input,loading,user,anonCount,activeConv,sessionId]);

  const handleKeyDown=(e)=>{ if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendMessage();} };
  const handleAuthSuccess=(u)=>{ setUser(u); setShowAuth(false); };
  const userInitials = user?.username?.slice(0,2)?.toUpperCase()||'?';

  // Assistant mesajlarına index ver
  let assistantIdx = -1;

  return (
    <div className="app-layout">
      <div className="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="sidebar-logo-icon">🐾</div>
            <div>
              <div className="sidebar-logo-text">VetAnimalAI</div>
              <div className="sidebar-logo-sub">Veterinerlik Platformu</div>
            </div>
          </div>
          <button className="new-chat-btn" onClick={newChat}>✏️ Yeni Sohbet</button>
        </div>
        <div className="sidebar-section"><div className="sidebar-section-label">Sohbet Geçmişi</div></div>
        <div className="conv-list">
          {conversations.length===0&&<div style={{fontSize:'12px',color:'var(--text-muted)',padding:'8px 6px'}}>Henüz sohbet yok.</div>}
          {conversations.map(c=>(
            <div key={c.id} className={`conv-item ${activeConv===c.id?'active':''}`} onClick={()=>setActiveConv(c.id)}>
              {c.title||'Sohbet'}
            </div>
          ))}
        </div>
        <div className="sidebar-footer">
          {user?(
            <div className="user-badge">
              <div className="user-avatar">{userInitials}</div>
              <div style={{flex:1,minWidth:0}}>
                <div className="user-name">{user.username}</div>
                <div className="user-role">{ROLE_LABELS[user.role]||user.role}</div>
              </div>
              <button onClick={logout} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:'13px',padding:'4px'}}>↩</button>
            </div>
          ):(
            <div className="login-prompt">
              <a onClick={()=>setShowAuth(true)}>Giriş yapın</a> veya{' '}
              <a onClick={()=>setShowAuth(true)}>Kayıt olun</a>
            </div>
          )}
        </div>
      </div>
      <div className="main-area">
        <div className="main-header">
          <span className="header-title">{user?`${ROLE_LABELS[user.role]||''} modu`:'Misafir modu'}</span>
          <div className="header-badge"><div className="header-badge-dot"/>Çok Ajanlı Sistem Aktif</div>
        </div>
        <div className="chat-area" ref={chatRef}>
          {messages.length===0?(
            <div className="welcome-screen">
              <div className="welcome-icon">🐾</div>
              <div className="welcome-title">VetAnimalAI'ye Hoş Geldiniz</div>
              <div className="welcome-sub">Veterinerlik ve hayvancılık alanında uzman yapay zeka danışmanlığı. Her sorunuz birden fazla uzman ajan tarafından değerlendirilir.</div>
              <div className="suggestion-grid">
                {SUGGESTIONS.map((s,i)=>(
                  <div key={i} className="suggestion-card" onClick={()=>sendMessage(s.text)}>
                    <div className="suggestion-icon">{s.icon}</div>
                    <div className="suggestion-text">{s.text}</div>
                  </div>
                ))}
              </div>
            </div>
          ):(
            <>
              {messages.map((msg,i)=>{
                if(msg.type==='assistant') assistantIdx++;
                const aIdx = assistantIdx;
                return msg.type==='user'?(
                  <div key={i} className="message-wrapper">
                    <div className="message-user">
                      <div className="message-user-bubble">{msg.content}</div>
                    </div>
                  </div>
                ):(
                  <AgentResponse
                    key={i} data={msg.data}
                    feedbackState={feedbacks[aIdx]??null}
                    onFeedback={(vote)=>sendFeedback(aIdx, vote, msg.data)}
                  />
                );
              })}
              {loading&&<LoadingIndicator step={loadStep}/>}
            </>
          )}
        </div>
        <div className="input-area">
          <div className="input-wrapper">
            <div className="input-box">
              <textarea ref={textareaRef} className="input-textarea"
                placeholder="Veterinerlik sorunuzu yazın... (Enter ile gönderin)"
                value={input}
                onChange={e=>{setInput(e.target.value);e.target.style.height='auto';e.target.style.height=Math.min(e.target.scrollHeight,160)+'px';}}
                onKeyDown={handleKeyDown} disabled={loading} rows={1}/>
              <button className="send-btn" onClick={()=>sendMessage()} disabled={loading||!input.trim()}>
                {loading?(
                  <svg width="16" height="16" viewBox="0 0 16 16">
                    <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="20" strokeDashoffset="0">
                      <animateTransform attributeName="transform" type="rotate" from="0 8 8" to="360 8 8" dur="0.8s" repeatCount="indefinite"/>
                    </circle>
                  </svg>
                ):(
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M1 14L8 2l7 12-7-3-7 3z"/></svg>
                )}
              </button>
            </div>
            <div className="input-meta">
              <span className="input-hint">Enter ile gönder · Shift+Enter yeni satır</span>
              {!user&&<span className="anon-counter">{2-anonCount} ücretsiz soru kaldı</span>}
            </div>
          </div>
        </div>
      </div>
      {showAuth&&<AuthModal onClose={()=>setShowAuth(false)} onSuccess={handleAuthSuccess}/>}
    </div>
  );
}
